import { create } from "zustand";
import { devtools } from "zustand/middleware";

export interface ChatWindowFilter {
	selectedCategoriesFilter: string[];
	selectedPropertiesFilter: string[];
	selectedProductsFilter: string[];
	selectedWarrantyStatusFilter: string[];
	installationDateStart: Date | null;
	installationDateEnd: Date | null;
	searchInstalledProductsTerm: string;
	gridView: boolean;
}

interface ChatWindowFiltersState {
	// State
	filters: ChatWindowFilter;

	// Actions
	setFilters: (newFilters: Partial<ChatWindowFilter>) => void;
	clearFilters: () => void;
	setGridView: (gridView: boolean) => void;
	setSearchTerm: (term: string) => void;
	setCategoryFilters: (categories: string[]) => void;
	setPropertyFilters: (properties: string[]) => void;
	setProductsFilters: (products: string[]) => void;
	setWarrantyStatusFilters: (statuses: string[]) => void;
	setDateRange: (start: Date | null, end: Date | null) => void;
}

const initialFilters: ChatWindowFilter = {
	selectedCategoriesFilter: [],
	selectedPropertiesFilter: [],
	selectedProductsFilter: [],
	selectedWarrantyStatusFilter: [],
	installationDateStart: null,
	installationDateEnd: null,
	searchInstalledProductsTerm: "",
	gridView: true
};

// DevTools configuration options
const devToolsOptions = {
	// Name of the store in Redux DevTools (helps when you have multiple stores)
	name: "ChatWindowFilters",

	// Enable/disable DevTools based on environment (optional)
	// You could use process.env.NODE_ENV !== 'production' here
	enabled: true,

	// Custom anonymous action type (optional)
	anonymousActionType: "UNKNOWN_ACTION",

	// Serialize options (optional - used for handling complex objects)
	serialize: {
		// Options for handling circular references
		options: true
	}
};

// State persists across React Router navigations (module singleton) and
// resets naturally on page refresh — no localStorage needed.
export const useChatWindowFiltersStore = create<ChatWindowFiltersState>()(
	// DevTools should be the outermost middleware
	devtools(
		set => ({
			// Initial state
			filters: initialFilters,

			// Actions
			setFilters: newFilters =>
				// The third parameter to set allows us to name the action for DevTools
				set(
					state => ({
						filters: { ...state.filters, ...newFilters }
					}),
					false,
					{
						type: "chat-window-filters/setFilters",
						payload: newFilters
					}
				),

			clearFilters: () => set({ filters: initialFilters }, false, "chat-window-filters/clearFilters"),

			setGridView: gridView =>
				set(
					state => ({
						filters: { ...state.filters, gridView }
					}),
					false,
					{
						type: "chat-window-filters/setGridView",
						payload: gridView
					}
				),

			setSearchTerm: searchInstalledProductsTerm =>
				set(
					state => ({
						filters: { ...state.filters, searchInstalledProductsTerm }
					}),
					false,
					{
						type: "chat-window-filters/setSearchTerm",
						payload: searchInstalledProductsTerm
					}
				),

			setCategoryFilters: selectedCategoriesFilter =>
				set(
					state => ({
						filters: { ...state.filters, selectedCategoriesFilter }
					}),
					false,
					{
						type: "chat-window-filters/setCategoryFilters",
						payload: selectedCategoriesFilter
					}
				),

			setPropertyFilters: selectedPropertiesFilter =>
				set(
					state => ({
						filters: { ...state.filters, selectedPropertiesFilter }
					}),
					false,
					{
						type: "chat-window-filters/setPropertyFilters",
						payload: selectedPropertiesFilter
					}
				),

			setProductsFilters: selectedProductsFilter =>
				set(
					state => ({
						filters: { ...state.filters, selectedProductsFilter }
					}),
					false,
					{
						type: "chat-window-filters/setProductsFilters",
						payload: selectedProductsFilter
					}
				),

			setWarrantyStatusFilters: selectedWarrantyStatusFilter =>
				set(
					state => ({
						filters: { ...state.filters, selectedWarrantyStatusFilter }
					}),
					false,
					{
						type: "chat-window-filters/setWarrantyStatusFilters",
						payload: selectedWarrantyStatusFilter
					}
				),

			setDateRange: (installationDateStart, installationDateEnd) =>
				set(
					state => ({
						filters: {
							...state.filters,
							installationDateStart,
							installationDateEnd
						}
					}),
					false,
					{
						type: "chat-window-filters/setDateRange",
						payload: {
							start: installationDateStart,
							end: installationDateEnd
						}
					}
				)
		}),
		// Pass DevTools options as the second parameter to devtools
		devToolsOptions
	)
);

/*
DevTools Usage Guide:

1. Installation:
   - Install Redux DevTools extension in your browser
   - No additional npm packages are required

2. Usage in components:
   - Your components can use the store normally without changes
   - Redux DevTools will automatically track state changes

3. Customization options:
   - Modify the devToolsOptions object to customize behavior:
     - Change 'name' to give your store a different name in the DevTools
     - Set 'enabled' to false in production to disable DevTools
     - Adjust 'serialize' settings for complex objects
     - Change 'anonymousActionType' to customize unnamed actions

4. Action naming:
   - Each action is now named with a descriptive type for better debugging
   - You can modify the action type strings to match your preferences
   - The naming pattern follows Redux conventions: 'domain/eventName'

5. Multiple stores:
   - If you have multiple stores, give each a unique name in devToolsOptions
   - You can group related stores by using a common prefix in their names

6. Debugging tips:
   - Use the "jump to state" feature in Redux DevTools to time-travel
   - Inspect the payload of each action to understand what changed
   - Use the "diff" view to see exactly what changed in each update
*/
